import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Plus, Network as NetworkIcon, Trash2, Pencil, Eye, Info } from 'lucide-react';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { SectionHeader } from '../components/ui/SectionHeader';

// Satu baris = satu network eksternal tempat kita jadi affiliator.
// Config di sini = dialek postback DEFAULT untuk semua offer network itu;
// offer bisa override per-field dari halaman Offers.
export interface AffiliateNetwork {
  id: number;
  name: string;
  notes: string | null;
  postbackMethod: 'GET' | 'POST' | 'BOTH';
  postbackAuthType: 'none' | 'hmac' | 'token' | 'ip_whitelist';
  postbackAuthValue: string | null;
  postbackParamClickId: string;
  postbackParamRevenue: string;
  postbackParamStatus: string;
  postbackParamSubId: string | null;
  postbackStatusApproved: string;
  postbackStatusRejected: string;
  postbackStatusPending: string | null;
  postbackResponseFormat: 'text' | 'json' | 'pixel';
  postbackResponseBody: string;
  attributionWindowDays: number;
  holdPeriodHours: number;
  offerCount?: number;
}

export function AffiliateNetworksPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [createOpen, setCreateOpen] = useState(false);
  const [editFor, setEditFor] = useState<AffiliateNetwork | null>(null);
  const [showSecret, setShowSecret] = useState<number | null>(null);

  const list = useQuery({
    queryKey: ['affiliate-networks'],
    queryFn: () => apiClient.get<AffiliateNetwork[]>('/api/v1/networks'),
  });

  const invalidate = () => {
    // Offers menampilkan param efektif yang mewarisi network — invalidasi
    // keduanya supaya dropdown/prefill tidak basi.
    qc.invalidateQueries({ queryKey: ['affiliate-networks'] });
    qc.invalidateQueries({ queryKey: ['offers'] });
  };

  const create = useMutation({
    mutationFn: (d: any) => apiClient.post('/api/v1/networks', d),
    onSuccess: () => {
      invalidate();
      setCreateOpen(false);
    },
  });
  const update = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      apiClient.patch(`/api/v1/networks/${id}`, data),
    onSuccess: () => {
      invalidate();
      setEditFor(null);
    },
  });
  const remove = useMutation({
    mutationFn: (id: number) => apiClient.delete(`/api/v1/networks/${id}`),
    onSuccess: invalidate,
  });
  const regenSecret = useMutation({
    mutationFn: (id: number) =>
      apiClient.post<{ secret: string }>(`/api/v1/networks/${id}/regenerate-secret`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['affiliate-networks'] }),
  });

  const handleDelete = async (n: AffiliateNetwork) => {
    const result = await confirm({
      title: `Delete ${n.name}?`,
      description:
        `${n.offerCount ?? 0} offer akan kehilangan link network ini dan kembali ke config default generiknya. Riwayat konversi tetap aman.`,
      variant: 'danger',
      actions: [{ label: 'Delete', variant: 'danger', result: 'delete', icon: <Trash2 size={14} /> }],
    });
    if (result === 'delete') remove.mutate(n.id);
  };

  return (
    <div className="w-full space-y-5 sm:space-y-6 animate-fade-in">
      <SectionHeader
        title="Affiliate Networks"
        subtitle={`${list.data?.length ?? 0} network — dialek postback diisi sekali, semua offer mewarisi`}
        actions={
          <Button icon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>New Network</Button>
        }
      />

      <Card className="p-0">
        <div className="overflow-x-auto">
          <table className="table-cyber">
            <thead>
              <tr>
                <th>Network</th>
                <th>Dialek Postback (param → nilai status)</th>
                <th>Auth</th>
                <th className="text-right">Offers</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((n) => (
                <tr key={n.id}>
                  <td>
                    <div className="font-display font-bold text-sm text-neon-cyan">{n.name}</div>
                    {n.notes && <div className="text-[11px] text-text-tertiary">{n.notes}</div>}
                  </td>
                  <td className="font-mono text-[10px] text-text-secondary leading-relaxed">
                    click=<span className="text-neon-green">{n.postbackParamClickId}</span>{' '}
                    sub=<span className="text-neon-green">{n.postbackParamSubId || 'sub_id'}</span>{' '}
                    rev=<span className="text-neon-green">{n.postbackParamRevenue}</span>{' '}
                    st=<span className="text-neon-green">{n.postbackParamStatus}</span>
                    <br />
                    ok=<code>{n.postbackStatusApproved}</code>{' '}
                    pend=<code>{n.postbackStatusPending || 'pending'}</code>{' '}
                    rej=<code>{n.postbackStatusRejected}</code>
                  </td>
                  <td>
                    {n.postbackAuthType === 'none' ? (
                      <span className="text-text-tertiary font-mono text-xs">none</span>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <span className="text-[9px] text-neon-yellow uppercase">{n.postbackAuthType}</span>
                        {(n.postbackAuthType === 'hmac' || n.postbackAuthType === 'token') && (
                          <>
                            <button
                              onClick={() => setShowSecret(showSecret === n.id ? null : n.id)}
                              className="text-text-tertiary hover:text-neon-cyan"
                              title="Show secret"
                            >
                              <Eye size={12} />
                            </button>
                            {n.postbackAuthType === 'hmac' && (
                              <button
                                onClick={() => regenSecret.mutate(n.id)}
                                className="text-[9px] text-text-tertiary hover:text-neon-magenta uppercase"
                                title="Regenerate HMAC secret"
                              >
                                regen
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                    {showSecret === n.id && n.postbackAuthValue && (
                      <code className="block mt-1 text-[10px] font-mono text-neon-yellow break-all">
                        secret: {n.postbackAuthValue}
                      </code>
                    )}
                  </td>
                  <td className="text-right font-mono text-xs text-text-secondary">
                    {n.offerCount ?? 0}
                  </td>
                  <td className="text-right">
                    <div className="inline-flex items-center gap-1 justify-end">
                      <Button variant="ghost" size="sm" icon={<Pencil size={12} />} onClick={() => setEditFor(n)}>
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<Trash2 size={12} />}
                        onClick={() => handleDelete(n)}
                        title="Delete network"
                      />
                    </div>
                  </td>
                </tr>
              ))}
              {(list.data ?? []).length === 0 && !list.isLoading && (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-text-tertiary">
                    <NetworkIcon className="mx-auto mb-2" size={32} />
                    <p className="font-mono text-xs">belum ada network — tambahkan satu (mis. Trafee, iMonetizeIt, LosPollos)</p>
                    <p className="font-mono text-[10px] text-text-tertiary mt-1">
                      Offer yang terhubung ke network mewarisi config nama param &amp; nilai status otomatis.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {createOpen && (
        <NetworkFormModal
          open={createOpen}
          mode="create"
          onClose={() => setCreateOpen(false)}
          onSubmit={(d) => create.mutate(d)}
          loading={create.isPending}
        />
      )}
      {editFor !== null && (
        <NetworkFormModal
          open={editFor !== null}
          mode="edit"
          network={editFor}
          onClose={() => setEditFor(null)}
          onSubmit={(d) => editFor && update.mutate({ id: editFor.id, data: d })}
          loading={update.isPending}
        />
      )}
    </div>
  );
}

function NetworkFormModal({
  open,
  mode,
  network,
  onClose,
  onSubmit,
  loading,
}: {
  open: boolean;
  mode: 'create' | 'edit';
  network?: AffiliateNetwork | null;
  onClose: () => void;
  onSubmit: (data: any) => void;
  loading: boolean;
}) {
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [paramClickId, setParamClickId] = useState('click_id');
  const [paramSubId, setParamSubId] = useState('sub_id');
  const [paramRevenue, setParamRevenue] = useState('revenue');
  const [paramStatus, setParamStatus] = useState('status');
  const [statusApproved, setStatusApproved] = useState('approved');
  const [statusRejected, setStatusRejected] = useState('rejected');
  const [statusPending, setStatusPending] = useState('pending');

  // Pola sama dengan OfferFormModal: sync dari prop saat modal terbuka.
  // Parent me-mount ulang modal tiap kali dibuka (conditional render), jadi
  // state selalu mulai bersih untuk mode create.
  useEffect(() => {
    if (open && mode === 'edit' && network) {
      setName(network.name);
      setNotes(network.notes ?? '');
      setParamClickId(network.postbackParamClickId);
      setParamSubId(network.postbackParamSubId ?? 'sub_id');
      setParamRevenue(network.postbackParamRevenue);
      setParamStatus(network.postbackParamStatus);
      setStatusApproved(network.postbackStatusApproved);
      setStatusRejected(network.postbackStatusRejected);
      setStatusPending(network.postbackStatusPending ?? 'pending');
    }
  }, [open, mode, network]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'edit' ? `Edit Network: ${network?.name}` : 'New Affiliate Network'}
      size="lg"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({
            name,
            notes: notes || undefined,
            postbackParamClickId: paramClickId,
            postbackParamSubId: paramSubId,
            postbackParamRevenue: paramRevenue,
            postbackParamStatus: paramStatus,
            postbackStatusApproved: statusApproved,
            postbackStatusRejected: statusRejected,
            postbackStatusPending: statusPending || undefined,
          });
        }}
        className="w-full space-y-4"
      >
        {mode === 'create' && (
          <div className="px-3.5 py-2.5 border border-neon-cyan/30 bg-neon-cyan/5 rounded text-[11px] font-mono text-text-secondary leading-relaxed flex gap-2 items-start">
            <Info size={14} className="inline shrink-0 text-neon-cyan mt-0.5" /> Isi dialek makro network SEKALI di sini. Semua offer di bawah network ini otomatis
            mewaris — kecuali field yang di-override eksplisit di halaman Offers.
          </div>
        )}

        <Input label="Network Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Trafee" required />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Input label="Click ID param" value={paramClickId} onChange={(e) => setParamClickId(e.target.value)} hint="Trafee: ext_click_id" />
          <Input label="Sub ID param" value={paramSubId} onChange={(e) => setParamSubId(e.target.value)} hint="iMonet: aff_sub" />
          <Input label="Revenue param" value={paramRevenue} onChange={(e) => setParamRevenue(e.target.value)} hint="Trafee: sum" />
          <Input label="Status param" value={paramStatus} onChange={(e) => setParamStatus(e.target.value)} hint="default: status" />
        </div>

        <p className="text-[10px] font-mono text-text-tertiary">Nilai yang dianggap approved / pending / rejected:</p>
        <div className="grid grid-cols-3 gap-3">
          <Input label="Approved value" value={statusApproved} onChange={(e) => setStatusApproved(e.target.value)} hint="contoh: approved, 1" />
          <Input label="Pending value" value={statusPending} onChange={(e) => setStatusPending(e.target.value)} hint="contoh: hold, 2" />
          <Input label="Rejected value" value={statusRejected} onChange={(e) => setStatusRejected(e.target.value)} hint="contoh: rejected, 0" />
        </div>

        <Input label="Catatan (opsional)" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="akun, syarat payout, dll" />

        <div className="flex gap-2 justify-end pt-2 border-t border-border-subtle">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" loading={loading} icon={mode === 'edit' ? <Pencil size={14} /> : <Plus size={14} />}>
            {mode === 'edit' ? 'Save Changes' : 'Add Network'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
